import '../scss/app.scss'; // Import SCSS
import React from 'react';
import ReactDOM from 'react-dom/client'; // ✅ React 18 API
import { initPortalButtonTracking } from './portal-button-tracker';

const SOCIAL_LINK_LABELS = [
  { test: href => /facebook\.com/i.test(href), label: 'Facebook' },
  { test: href => /linkedin\.com/i.test(href), label: 'LinkedIn' },
  { test: href => /youtube\.com|youtu\.be/i.test(href), label: 'YouTube' },
  { test: href => /instagram\.com/i.test(href), label: 'Instagram' },
  { test: href => /twitter\.com|x\.com/i.test(href), label: 'X' },
  { test: href => /tiktok\.com/i.test(href), label: 'TikTok' },
  { test: href => /pinterest\.com/i.test(href), label: 'Pinterest' },
  { test: href => /vimeo\.com/i.test(href), label: 'Vimeo' },
  { test: href => /flickr\.com/i.test(href), label: 'Flickr' },
  { test: href => /github\.com/i.test(href), label: 'GitHub' },
  { test: href => /behance\.net/i.test(href), label: 'Behance' },
  { test: href => /whatsapp\.com|wa\.me/i.test(href), label: 'WhatsApp' },
  { test: href => /soundcloud\.com/i.test(href), label: 'SoundCloud' },
];

function humanizeSlug(slug) {
  return slug
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function getLabelFromHref(href) {
  if (!href) {
    return '';
  }

  for (const rule of SOCIAL_LINK_LABELS) {
    if (rule.test(href)) {
      return rule.label;
    }
  }

  try {
    const url = new URL(href, window.location.href);
    const pathname = url.pathname.replace(/\/+$/, '');
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || url.hostname.replace(/^www\./i, '');
    const label = humanizeSlug(lastSegment);

    if (label) {
      return label;
    }
  } catch (error) {
    // Ignore invalid URLs and fall back below.
  }

  return '';
}

function hasVisibleText(link) {
  const text = (link.textContent || '').replace(/\s+/g, ' ').trim();
  return text.length > 0;
}

function labelIconOnlyLinks(root = document) {
  const links = root.querySelectorAll ? root.querySelectorAll('a[href]') : [];

  links.forEach(link => {
    if (link.hasAttribute('aria-label')) {
      return;
    }

    if (hasVisibleText(link)) {
      return;
    }

    const label = getLabelFromHref(link.getAttribute('href'));
    if (!label) {
      return;
    }

    link.setAttribute('aria-label', label);
  });
}

function getFirstTwoWords(text) {
  const cleaned = (text || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^['"“”‘’]+|['"“”‘’]+$/g, '');

  if (!cleaned) {
    return '';
  }

  const nameOnly = cleaned.split(/\s*(?:,|\||\s+[–—]\s+|\s+-\s+|\s*\().*/u, 2)[0].trim();
  const parts = nameOnly.split(/\s+/).filter(Boolean);

  if (!parts.length) {
    return '';
  }

  if (parts.length === 1) {
    return parts[0];
  }

  return `${parts[0]} ${parts[1]}`;
}

function getHeadshotAltFromContext(img) {
  const wrapper = img.closest('.team-headshot');
  const candidate =
    wrapper?.getAttribute('data-name') ||
    wrapper?.getAttribute('aria-label') ||
    wrapper?.textContent ||
    img.getAttribute('alt') ||
    '';

  const personName = getFirstTwoWords(candidate);
  return personName ? `${personName}'s Headshot` : 'Headshot';
}

function normalizeTeamHeadshots(root = document) {
  const context = root || document;
  const images = context.querySelectorAll ? Array.from(context.querySelectorAll('.team-headshot img')) : [];

  if (context.matches && context.matches('.team-headshot img')) {
    images.unshift(context);
  }

  images.forEach(img => {
    if (!(img instanceof HTMLImageElement)) {
      return;
    }

    const altText = getHeadshotAltFromContext(img);
    if (altText) {
      img.setAttribute('alt', altText);
    }
  });
}

function injectServiceCategoryStyles() {
  if (document.getElementById('trust-service-category-a11y-fixes')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'trust-service-category-a11y-fixes';
  style.textContent = `
    .service-content .x-text-content-text-primary a {
      color: var(--brand-primary) !important;
      text-decoration-color: currentColor;
    }

    .service-content .x-text-content-text-primary a.btn,
    .service-content .x-text-content-text-primary .btn,
    .service-content a.btn,
    .service-content .btn {
      color: var(--brand-white) !important;
      -webkit-text-fill-color: var(--brand-white) !important;
    }

    .service-content .x-text-content-text-primary a:hover,
    .service-content .x-text-content-text-primary a:focus-visible {
      color: var(--brand-secondary) !important;
    }

    .service-content .x-text-content-text-primary h3,
    .service-content .x-text-content-text-primary h4 {
      color: var(--brand-primary) !important;
    }

    .service-content .btn-interact,
    .service-content .btn-alt {
      background: var(--brand-primary) !important;
      color: var(--brand-white) !important;
    }

    .service-content .btn-interact:hover,
    .service-content .btn-interact:focus-visible,
    .service-content .btn-alt:hover,
    .service-content .btn-alt:focus-visible {
      background: var(--brand-secondary) !important;
      color: var(--brand-white) !important;
    }
  `;

  document.head.appendChild(style);
}

function normalizeServiceCategoryResources(root = document) {
  const context = root || document;
  const serviceRoots = context.querySelectorAll ? Array.from(context.querySelectorAll('.service-content')) : [];

  if (context.matches && context.matches('.service-content')) {
    serviceRoots.unshift(context);
  }

  if (!serviceRoots.length) {
    return;
  }

  injectServiceCategoryStyles();

  serviceRoots.forEach(serviceRoot => {
    const headings = serviceRoot.querySelectorAll('.x-text-content-text-primary h4');

    headings.forEach(heading => {
      if (heading.dataset.trustNormalizedHeading === 'true') {
        return;
      }

      const replacement = document.createElement('h3');
      replacement.className = heading.className;
      replacement.innerHTML = heading.innerHTML;
      replacement.dataset.trustNormalizedHeading = 'true';
      heading.replaceWith(replacement);
    });
  });
}

function injectContactUsStyles() {
  if (document.getElementById('trust-contact-us-a11y-fixes')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'trust-contact-us-a11y-fixes';
  style.textContent = `
    body.trust-contact-us-page a[href$="contact-us"] .x-anchor-text-primary {
      color: var(--brand-primary) !important;
      -webkit-text-fill-color: var(--brand-primary) !important;
    }

    body.trust-contact-us-page a[href$="contact-us"]:hover .x-anchor-text-primary,
    body.trust-contact-us-page a[href$="contact-us"]:focus-visible .x-anchor-text-primary {
      color: var(--brand-secondary) !important;
      -webkit-text-fill-color: var(--brand-secondary) !important;
    }

    body.trust-contact-us-page .trust-contact-us-office-card h2,
    body.trust-contact-us-page .trust-contact-us-office-card h3 {
      font-family: var(--font-family-header) !important;
      font-size: 24px !important;
      font-weight: 700 !important;
      line-height: 1.1 !important;
      letter-spacing: -0.02em !important;
      color: var(--brand-primary) !important;
      margin: 0 0 1.125rem 0 !important;
    }

    body.trust-contact-us-page .trust-contact-us-office-card .trust-contact-us-address {
      color: var(--brand-text) !important;
      font-family: var(--font-family-body) !important;
      font-size: 16px !important;
      line-height: 1.5 !important;
      margin-bottom: 1rem !important;
    }

    body.trust-contact-us-page .trust-contact-us-office-card .trust-contact-us-hours {
      color: var(--brand-text) !important;
      font-family: var(--font-family-body) !important;
      font-size: 16px !important;
      line-height: 1.5 !important;
      margin-bottom: 1rem !important;
      font-weight: 600 !important;
    }

    body.trust-contact-us-page .trust-contact-us-office-card .trust-contact-us-contact a {
      color: var(--brand-secondary) !important;
      font-family: var(--font-family-body) !important;
      font-size: 18px !important;
      font-weight: 400 !important;
      text-decoration: none !important;
    }

    body.trust-contact-us-page .trust-contact-us-office-card .trust-contact-us-map-link {
      color: var(--brand-secondary) !important;
      font-family: var(--font-family-body) !important;
      font-size: 16px !important;
      font-weight: 400 !important;
      text-decoration: none !important;
    }

    body.trust-contact-us-page .gform_wrapper .gform_validation_errors,
    body.trust-contact-us-page .gform_wrapper .validation_error,
    body.trust-contact-us-page .gform_wrapper .trust-contact-form-error-summary {
      background: rgba(21, 52, 90, 0.06) !important;
      border: 1px solid rgba(21, 52, 90, 0.16) !important;
      border-radius: var(--site-border-radius) !important;
      color: var(--brand-text) !important;
      margin-bottom: 1rem !important;
      padding: 1rem 1.125rem !important;
    }

    body.trust-contact-us-page .gform_wrapper .trust-contact-form-error-summary a {
      color: var(--brand-secondary) !important;
      text-decoration: underline !important;
      text-underline-offset: 0.16em !important;
    }

    body.trust-contact-us-page .gform_wrapper .gfield_description,
    body.trust-contact-us-page .gform_wrapper .validation_message,
    body.trust-contact-us-page .gform_wrapper .gfield_validation_message {
      color: var(--brand-text) !important;
      font-size: 0.95rem !important;
      line-height: 1.45 !important;
    }

    body.trust-contact-us-page .gform_wrapper input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="submit"]),
    body.trust-contact-us-page .gform_wrapper select,
    body.trust-contact-us-page .gform_wrapper textarea {
      border: 1px solid rgba(21, 52, 90, 0.18) !important;
      border-radius: var(--site-border-radius) !important;
      color: var(--brand-text) !important;
      transition: border-color 0.2s ease, box-shadow 0.2s ease, outline-color 0.2s ease !important;
    }

    body.trust-contact-us-page .gform_wrapper input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="submit"]):focus-visible,
    body.trust-contact-us-page .gform_wrapper select:focus-visible,
    body.trust-contact-us-page .gform_wrapper textarea:focus-visible,
    body.trust-contact-us-page .gform_wrapper button:focus-visible,
    body.trust-contact-us-page .gform_wrapper a:focus-visible {
      outline: 3px solid var(--brand-secondary) !important;
      outline-offset: 3px !important;
    }

    body.trust-contact-us-page .gform_wrapper input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="submit"]):focus-visible,
    body.trust-contact-us-page .gform_wrapper select:focus-visible,
    body.trust-contact-us-page .gform_wrapper textarea:focus-visible {
      border-color: var(--brand-secondary) !important;
      box-shadow: 0 0 0 2px rgba(37, 97, 65, 0.12) !important;
    }

    body.trust-contact-us-page .gfield_error input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="submit"]),
    body.trust-contact-us-page .gfield_error select,
    body.trust-contact-us-page .gfield_error textarea {
      border-color: var(--brand-secondary) !important;
    }

    body.trust-contact-us-page .gform_footer .gform_button,
    body.trust-contact-us-page .gform_footer input[type="submit"],
    body.trust-contact-us-page .gform_wrapper .button,
    body.trust-contact-us-page .gform_wrapper button {
      background: var(--brand-primary) !important;
      border: none !important;
      border-radius: var(--site-border-radius) !important;
      color: var(--brand-white) !important;
      -webkit-text-fill-color: var(--brand-white) !important;
      text-transform: uppercase !important;
      transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease !important;
    }

    body.trust-contact-us-page .gform_footer .gform_button:hover,
    body.trust-contact-us-page .gform_footer .gform_button:focus-visible,
    body.trust-contact-us-page .gform_footer input[type="submit"]:hover,
    body.trust-contact-us-page .gform_footer input[type="submit"]:focus-visible,
    body.trust-contact-us-page .gform_wrapper .button:hover,
    body.trust-contact-us-page .gform_wrapper .button:focus-visible,
    body.trust-contact-us-page .gform_wrapper button:hover,
    body.trust-contact-us-page .gform_wrapper button:focus-visible {
      background: var(--brand-secondary) !important;
      color: var(--brand-white) !important;
      -webkit-text-fill-color: var(--brand-white) !important;
      transform: translateY(-2px) !important;
    }
  `;

  document.head.appendChild(style);
}

function ensureElementId(element, prefix) {
  if (!element.id) {
    const suffix = Math.random().toString(36).slice(2, 9);
    element.id = `${prefix}-${suffix}`;
  }

  return element.id;
}

function getFieldLabel(field) {
  const label = field.querySelector('label');
  if (!label) {
    return '';
  }

  return (label.textContent || '').replace(/\s+/g, ' ').trim();
}

function enhanceContactUsField(field) {
  const controls = Array.from(field.querySelectorAll('input:not([type="hidden"]):not([type="submit"]), select, textarea'));
  if (!controls.length) {
    return;
  }

  const label = field.querySelector('label');
  const helperNodes = Array.from(
    field.querySelectorAll('.gfield_description, .validation_message, .gfield_validation_message')
  ).filter(node => (node.textContent || '').trim().length > 0);
  const fieldIsInvalid = field.classList.contains('gfield_error') || field.getAttribute('aria-invalid') === 'true';

  if (label && controls.length === 1) {
    const control = controls[0];
    const controlId = ensureElementId(control, 'trust-contact-field');
    if (!label.id) {
      label.id = `${controlId}-label`;
    }

    if (!label.hasAttribute('for')) {
      label.setAttribute('for', controlId);
    }

    if (!control.hasAttribute('aria-label') && !control.hasAttribute('aria-labelledby')) {
      control.setAttribute('aria-labelledby', label.id);
    }
  }

  controls.forEach(control => {
    const describedBy = new Set(
      (control.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .map(token => token.trim())
        .filter(Boolean)
    );

    helperNodes.forEach(node => {
      const nodeId = ensureElementId(node, 'trust-contact-field-help');
      describedBy.add(nodeId);
    });

    if (describedBy.size > 0) {
      control.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    }

    if (fieldIsInvalid) {
      control.setAttribute('aria-invalid', 'true');
    } else if (control.getAttribute('aria-invalid') === 'true') {
      control.setAttribute('aria-invalid', 'false');
    }
  });
}

function buildContactUsErrorSummary(form) {
  const invalidFields = Array.from(form.querySelectorAll('.gfield_error'));
  if (!invalidFields.length) {
    const existingSummary = form.querySelector('.trust-contact-form-error-summary');
    if (existingSummary) {
      existingSummary.remove();
    }
    return null;
  }

  let summary = form.querySelector('.trust-contact-form-error-summary');
  if (!summary) {
    summary = document.createElement('div');
    summary.className = 'trust-contact-form-error-summary';
    summary.setAttribute('tabindex', '-1');
    summary.setAttribute('role', 'alert');
    summary.setAttribute('aria-live', 'assertive');
    form.insertBefore(summary, form.firstChild);
  }

  const listItems = invalidFields
    .map(field => {
      const fieldLabel = getFieldLabel(field) || 'This field';
      const fieldTarget = field.querySelector('input:not([type="hidden"]), select, textarea');
      const targetId = fieldTarget ? ensureElementId(fieldTarget, 'trust-contact-field') : '';
      const validationMessage = field.querySelector('.gfield_validation_message, .validation_message');
      const message = validationMessage ? (validationMessage.textContent || '').replace(/\s+/g, ' ').trim() : 'Please review this field.';

      if (!targetId) {
        return `<li>${fieldLabel}: ${message}</li>`;
      }

      return `<li><a href="#${targetId}">${fieldLabel}</a> ${message}</li>`;
    })
    .join('');

  summary.innerHTML = `
    <strong>There was a problem with your submission.</strong>
    <p>Please review the fields below and try again.</p>
    <ul>${listItems}</ul>
  `;

  return summary;
}

function focusContactUsValidationState(form) {
  const summary = form.querySelector('.trust-contact-form-error-summary');
  const firstInvalid = form.querySelector('.gfield_error input:not([type="hidden"]), .gfield_error select, .gfield_error textarea');

  if (summary) {
    summary.focus();
    return;
  }

  if (firstInvalid) {
    firstInvalid.focus();
  }
}

function enhanceContactUsForms(root = document) {
  const context = root || document;
  const forms = context.querySelectorAll ? Array.from(context.querySelectorAll('.gform_wrapper form, form.gform_wrapper, form')) : [];

  forms.forEach(form => {
    if (form.dataset.trustContactFormEnhanced === 'true') {
      return;
    }

    form.dataset.trustContactFormEnhanced = 'true';

    const fieldWrappers = Array.from(form.querySelectorAll('.gfield'));
    fieldWrappers.forEach(field => enhanceContactUsField(field));

    const summary = buildContactUsErrorSummary(form);
    if (summary) {
      focusContactUsValidationState(form);
    }

    form.addEventListener('submit', () => {
      window.setTimeout(() => {
        fieldWrappers.forEach(field => enhanceContactUsField(field));
        const nextSummary = buildContactUsErrorSummary(form);
        if (nextSummary) {
          focusContactUsValidationState(form);
        }
      }, 50);
    });
  });
}

function markContactUsPage() {
  if (document.body) {
    document.body.classList.add('trust-contact-us-page');
  }
}

function normalizeContactUsOfficeHeadings(root = document) {
  const context = root || document;
  const cards = context.querySelectorAll ? Array.from(context.querySelectorAll('.trust-contact-us-office-card')) : [];

  if (context.matches && context.matches('.trust-contact-us-office-card')) {
    cards.unshift(context);
  }

  if (!cards.length) {
    return;
  }

  injectContactUsStyles();

  cards.forEach(card => {
    Array.from(card.children)
      .filter(child => child.tagName === 'H3')
      .forEach(heading => {
        if (heading.dataset.trustNormalizedHeading === 'true') {
          return;
        }

        const replacement = document.createElement('h2');
        replacement.className = heading.className;
        replacement.innerHTML = heading.innerHTML;
        replacement.dataset.trustNormalizedHeading = 'true';
        heading.replaceWith(replacement);
      });
  });
}

// Clean up account-top-link elements by removing <br> tags
function cleanAccountTopLinks() {
  const accountLinks = document.querySelectorAll('.account-top-link');
  
  accountLinks.forEach(link => {
    // Remove all <br> tags from the element
    const brTags = link.querySelectorAll('br');
    brTags.forEach(br => br.remove());
    
    // Also clean up any <br> tags in the HTML string
    link.innerHTML = link.innerHTML.replace(/<br\s*\/?>/gi, ' ');
    
    // Clean up extra whitespace
    link.innerHTML = link.innerHTML.replace(/\s+/g, ' ').trim();
  });
}

function initializeSidebarToggles() {
  const sidebarRoots = document.querySelectorAll('[data-sidebar-root]');
  if (!sidebarRoots.length) {
    return;
  }

  sidebarRoots.forEach(root => {
    const toggle = root.querySelector('.categories-toggle-btn');
    const overlay = root.querySelector('.categories-overlay');
    const closeBtn = root.querySelector('.categories-close-btn');

    if (!toggle || !overlay) {
      return;
    }

    const closeSidebar = () => {
      root.classList.remove('sidebar-open');
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', () => {
      const isOpen = root.classList.toggle('sidebar-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
    });

    overlay.addEventListener('click', closeSidebar);
    if (closeBtn) {
      closeBtn.addEventListener('click', closeSidebar);
    }
  });
}

// Run when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  markContactUsPage();
  cleanAccountTopLinks();
  initializeSidebarToggles();
  initPortalButtonTracking();
  labelIconOnlyLinks();
  normalizeContactUsOfficeHeadings();
  normalizeServiceCategoryResources();
  normalizeTeamHeadshots();
  enhanceContactUsForms();

  if (!window.__trustCompanyA11yObserver && document.body) {
    window.__trustCompanyA11yObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) {
          return;
        }

        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
          }

          markContactUsPage();
          labelIconOnlyLinks(node);
          normalizeContactUsOfficeHeadings(node);
          normalizeServiceCategoryResources(node);
          normalizeTeamHeadshots(node);
          enhanceContactUsForms(node);
        });
      });
    });

    window.__trustCompanyA11yObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});

// Also run cleanup after any dynamic content loads
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any new nodes contain account-top-link elements
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && node.classList.contains('account-top-link')) {
                cleanAccountTopLinks();
              } else if (node.querySelector && node.querySelector('.account-top-link')) {
                cleanAccountTopLinks();
              }

              labelIconOnlyLinks(node);
              normalizeContactUsOfficeHeadings(node);
              normalizeServiceCategoryResources(node);
              normalizeTeamHeadshots(node);
            }
          });
        }
  });
});

// Start observing changes to the document
observer.observe(document.body, {
  childList: true,
  subtree: true
});
 
const root = ReactDOM.createRoot(document.getElementById('root'));
 
const App = () => { 

  return "Hello, World!";
};


root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
